/**
 * Onglets et période de l'écran Argent (revue de l'écran Argent, variante A).
 *
 * Six onglets à un seul niveau, nommés comme les feuilles du classeur du
 * bureau. Les anciennes adresses (`?tab=apercu`, `a-traiter`, `analyses`,
 * `comptes`) mènent au nouvel onglet: les liens déjà partagés marchent encore.
 */

export const ONGLETS_ARGENT = [
  { value: 'a-faire', label: 'À faire', court: 'À faire' },
  { value: 'caisse', label: 'Caisse et banque', court: 'Caisse' },
  { value: 'clients', label: 'Clients', court: 'Clients' },
  { value: 'chauffeurs', label: 'Chauffeurs', court: 'Chauffeurs' },
  { value: 'fournisseurs', label: 'Fournisseurs', court: 'Fournisseurs' },
  { value: 'benefice', label: 'Bénéfice', court: 'Bénéfice' },
];

export const ONGLET_PAR_DEFAUT = 'a-faire';

export const ANCIENS_ONGLETS = {
  apercu: 'a-faire',
  'a-traiter': 'a-faire',
  analyses: 'benefice',
  comptes: 'caisse',
};

/** L'onglet à afficher pour la valeur `?tab=` reçue (ancienne ou inconnue comprise). */
export const ongletDepuis = (valeur) => {
  if (ONGLETS_ARGENT.some((onglet) => onglet.value === valeur)) return valeur;
  return ANCIENS_ONGLETS[valeur] || ONGLET_PAR_DEFAUT;
};

/** Le tiers choisi de chaque onglet vit dans l'adresse: `?tab=clients&client=…`. */
export const CLE_TIERS = {
  caisse: 'compte',
  clients: 'client',
  chauffeurs: 'chauffeur',
  fournisseurs: 'fournisseur',
};

// ---- Période -------------------------------------------------------------

export const PERIODES = ['mois', 'mois-dernier', 'trimestre', 'annee', 'tout', 'libre'];
export const PERIODE_PAR_DEFAUT = 'mois';

const iso = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const majuscule = (texte) => (texte ? texte.charAt(0).toUpperCase() + texte.slice(1) : texte);
const nomMois = (annee, mois) => majuscule(new Date(annee, mois, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }));
const moisCourt = (annee, mois) => new Date(annee, mois, 1).toLocaleDateString('fr-FR', { month: 'short' });
const dateCourte = (texte) => {
  const [a, m, j] = String(texte || '').split('-');
  return a && m && j ? `${j}/${m}` : '';
};
const DATE_ISO = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Bornes et libellés d'une période: `{ cle, du, au, libelle, court }`.
 * `libelle` nomme l'option du sélecteur (« Octobre 2026 »), `court` suit
 * « Bénéfice » dans la pastille de l'en-tête (« Bénéfice oct. »).
 */
export function bornesPeriode(cle, { du, au } = {}, aujourdhui = new Date()) {
  const annee = aujourdhui.getFullYear();
  const mois = aujourdhui.getMonth();
  const fin = iso(aujourdhui);
  switch (cle) {
    case 'mois-dernier': {
      const debut = new Date(annee, mois - 1, 1);
      const dernier = new Date(annee, mois, 0);
      return {
        cle,
        du: iso(debut),
        au: iso(dernier),
        libelle: nomMois(debut.getFullYear(), debut.getMonth()),
        court: moisCourt(debut.getFullYear(), debut.getMonth()),
      };
    }
    case 'trimestre': {
      const premier = Math.floor(mois / 3) * 3;
      const trimestre = `T${premier / 3 + 1} ${annee}`;
      return { cle, du: iso(new Date(annee, premier, 1)), au: fin, libelle: trimestre, court: trimestre };
    }
    case 'annee':
      return { cle, du: `${annee}-01-01`, au: fin, libelle: `Année ${annee}`, court: String(annee) };
    // Sans borne: tout le livre de caisse, le bénéfice cumulé.
    case 'tout':
      return { cle, du: '', au: '', libelle: 'Toutes les dates', court: 'cumulé' };
    case 'libre': {
      const debut = DATE_ISO.test(du || '') ? du : iso(new Date(annee, mois, 1));
      const borne = DATE_ISO.test(au || '') ? au : fin;
      return {
        cle,
        du: debut,
        au: borne,
        libelle: 'Dates libres',
        court: `du ${dateCourte(debut)} au ${dateCourte(borne)}`,
      };
    }
    default:
      return {
        cle: PERIODE_PAR_DEFAUT,
        du: iso(new Date(annee, mois, 1)),
        au: fin,
        libelle: nomMois(annee, mois),
        court: moisCourt(annee, mois),
      };
  }
}
